import { Minus, Plus } from 'lucide-react';

import cn from '@/utils/cn';

interface NumberStepperProps {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Show an empty field instead of "0" - the "unset" convention for optionals. */
  hideZero?: boolean;
  inputWidthClassName?: string;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  /** 'sm' renders a shorter, narrower stepper for inline/compact contexts */
  size?: 'default' | 'sm';
  className?: string;
}

// Numeric input with +/- buttons and no native spin arrows, used app-wide for
// every numeric field (the native spinners are also stripped globally in
// globals.css). Increment/decrement is clamped to [min, max].
const NumberStepper = ({
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  hideZero = false,
  inputWidthClassName = 'w-14',
  placeholder,
  disabled,
  ariaLabel,
  size = 'default',
  className,
}: NumberStepperProps) => {
  const clamp = (v: number): number => {
    const safe = Number.isFinite(v) ? v : min;
    const floored = Math.max(min, safe);
    return max !== undefined ? Math.min(max, floored) : floored;
  };

  const isSmall = size === 'sm';

  return (
    <div
      className={cn(
        // w-fit, not just inline-flex: a flex-column parent applies the default
        // `align-items: stretch`, which overrides an inline-flex child's
        // intrinsic width and blows the control out to the full row. Because the
        // -/+ buttons and the input are all fixed-width, that extra space landed
        // after the + button, leaving it stranded at the far right of the border
        // (visible under "Annual price" in the Add Product dialog). A stepper
        // should always be exactly as wide as its parts; a caller that genuinely
        // wants otherwise can still pass a width in `className` - twMerge lets
        // the later class win.
        'inline-flex w-fit items-center rounded-md border border-input bg-background shadow-sm',
        isSmall ? 'h-11 sm:h-6' : 'h-11 sm:h-8',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onChange(clamp(value - step))}
        disabled={disabled || value <= min}
        aria-label={ariaLabel ? `Decrease ${ariaLabel}` : 'Decrease'}
        className={cn(
          'flex h-full shrink-0 items-center justify-center rounded-l-md text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
          isSmall ? 'w-11 sm:w-5' : 'w-11 sm:w-7',
        )}
      >
        <Minus className={isSmall ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      </button>
      <input
        type="number"
        inputMode="numeric"
        value={hideZero && value === 0 ? '' : value}
        onChange={(e) => onChange(clamp(Number(e.target.value)))}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn(
          'h-full border-x border-input bg-transparent text-center font-semibold text-foreground',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
          '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          isSmall ? 'text-base sm:text-[11px]' : 'text-base sm:text-xs',
          inputWidthClassName,
        )}
      />
      <button
        type="button"
        onClick={() => onChange(clamp(value + step))}
        disabled={disabled || (max !== undefined && value >= max)}
        aria-label={ariaLabel ? `Increase ${ariaLabel}` : 'Increase'}
        className={cn(
          'flex h-full shrink-0 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
          isSmall ? 'w-11 sm:w-5' : 'w-11 sm:w-7',
        )}
      >
        <Plus className={isSmall ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      </button>
    </div>
  );
};

export default NumberStepper;
